// PricingAdmin.jsx — ABM del motor de precios (separadores):
// parámetro global, productos, formas de entrega y tramos de recargo.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";
const TIPOS = [
  { value: "NORMAL",       label: "Normal (costo fijo)" },
  { value: "DIRECTO_BSAS", label: "Directo Bs. As. (usa costo global)" },
];

const S = {
  page: { minHeight: "100vh", background: "#f7f4f6", padding: "32px 24px 48px", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  inner: { maxWidth: 900, margin: "0 auto" },
  title: { fontSize: 22, fontWeight: 700, color: "#222", marginBottom: 4 },
  sub: { fontSize: 13, color: "#888", marginBottom: 24 },
  card: { background: "#fff", borderRadius: 14, border: "1.5px solid #f0e6ec", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: 24, marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#9f0059", margin: "0 0 4px" },
  cardDesc: { fontSize: 12.5, color: "#888", margin: "0 0 16px" },
  input: { padding: "8px 12px", border: "1.5px solid #e0d6dc", borderRadius: 8, fontSize: 13, outline: "none", color: "#222" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { background: "#f7f4f6", color: "#666", fontWeight: 700, padding: "8px 10px", textAlign: "left", borderBottom: "1.5px solid #f0e6ec", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" },
  td: { padding: "8px 10px", borderBottom: "1px solid #f0e6ec", color: "#333", verticalAlign: "middle" },
  btnPrimary: { padding: "8px 18px", background: "#9f0059", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" },
  btnDashed: { background: "none", border: "1.5px dashed #c990b0", borderRadius: 7, color: "#9f0059", padding: "6px 14px", cursor: "pointer", fontWeight: 600, fontSize: 12 },
  btnIcon: { background: "none", border: "none", cursor: "pointer", fontSize: 13, padding: "3px 7px", borderRadius: 5 },
  error: { fontSize: 12.5, color: "#dc2626", fontWeight: 600, marginTop: 8 },
};

const uid = () => Math.random().toString(36).slice(2);

const PricingAdmin = () => {
  const [productos, setProductos] = useState([]);
  const [formas,    setFormas]    = useState([]);
  const [tramos,    setTramos]    = useState([]); // [{_k, porcentaje_desde, porcentaje_hasta, recargo(%)}]
  const [costoLog,  setCostoLog]  = useState("");
  const [loading,   setLoading]   = useState(true);

  // Edición inline de productos y formas: { [id|"new"]: {campos} }
  const [prodEdit, setProdEdit] = useState(null); // {id|null, nombre, cantidad_pallet, precio_lista}
  const [formaEdit, setFormaEdit] = useState(null);
  const [savingTramos, setSavingTramos] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    axios.post(`${BASE}/pricing_config_listar.php`)
      .then((r) => {
        if (!r.data?.success) { toast.error(r.data?.mensaje || "Error al cargar"); return; }
        setProductos(r.data.productos || []);
        setFormas(r.data.formas || []);
        setTramos((r.data.tramos || []).map((t) => ({
          _k: uid(),
          desde: String(parseFloat(t.porcentaje_desde)),
          hasta: String(parseFloat(t.porcentaje_hasta)),
          recargo: String(Math.round(parseFloat(t.recargo) * 10000) / 100), // como %
        })));
        setCostoLog(r.data.costo_logistico_pallet !== null ? String(r.data.costo_logistico_pallet) : "");
      })
      .catch(() => toast.error("Error de conexión"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Parámetro global ─────────────────────────────────────────────────────
  const guardarCosto = async () => {
    const r = await axios.post(`${BASE}/pricing_parametro_guardar.php`, new URLSearchParams({ costo_logistico_pallet: costoLog }));
    if (r.data?.success) toast.success("Costo logístico guardado");
    else toast.error(r.data?.mensaje || "Error");
  };

  // ── Productos ────────────────────────────────────────────────────────────
  const guardarProducto = async () => {
    const fd = new URLSearchParams({
      nombre: prodEdit.nombre ?? "", cantidad_pallet: prodEdit.cantidad_pallet ?? "", precio_lista: prodEdit.precio_lista ?? "",
    });
    if (prodEdit.id) fd.append("id", prodEdit.id);
    const r = await axios.post(`${BASE}/pricing_producto_guardar.php`, fd);
    if (r.data?.success) { toast.success("Producto guardado"); setProdEdit(null); cargar(); }
    else toast.error(r.data?.mensaje || "Error");
  };
  const eliminarProducto = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"?`)) return;
    const r = await axios.post(`${BASE}/pricing_producto_eliminar.php`, new URLSearchParams({ id: p.id }));
    if (r.data?.success) { toast.success("Producto eliminado"); cargar(); }
    else toast.error(r.data?.mensaje || "Error");
  };

  // ── Formas de entrega ────────────────────────────────────────────────────
  const guardarForma = async () => {
    const fd = new URLSearchParams({
      nombre: formaEdit.nombre ?? "", tipo_calculo: formaEdit.tipo_calculo ?? "NORMAL",
      costo_fijo: formaEdit.tipo_calculo === "DIRECTO_BSAS" ? "" : (formaEdit.costo_fijo ?? ""),
    });
    if (formaEdit.id) fd.append("id", formaEdit.id);
    const r = await axios.post(`${BASE}/pricing_forma_guardar.php`, fd);
    if (r.data?.success) { toast.success("Forma de entrega guardada"); setFormaEdit(null); cargar(); }
    else toast.error(r.data?.mensaje || "Error");
  };
  const eliminarForma = async (f) => {
    if (!window.confirm(`¿Eliminar "${f.nombre}"?`)) return;
    const r = await axios.post(`${BASE}/pricing_forma_eliminar.php`, new URLSearchParams({ id: f.id }));
    if (r.data?.success) { toast.success("Forma eliminada"); cargar(); }
    else toast.error(r.data?.mensaje || "Error");
  };

  // ── Tramos ───────────────────────────────────────────────────────────────
  const updTramo = (k, field, val) => setTramos((prev) => prev.map((t) => (t._k === k ? { ...t, [field]: val } : t)));
  const addTramo = () => setTramos((prev) => {
    const ultHasta = prev.length ? prev[prev.length - 1].hasta : "0";
    return [...prev, { _k: uid(), desde: ultHasta, hasta: "1", recargo: "10" }];
  });
  const delTramo = (k) => setTramos((prev) => prev.filter((t) => t._k !== k));

  // Validación en vivo de cobertura [0,1) sin huecos ni solapamientos
  const errorTramos = (() => {
    if (tramos.length === 0) return "Tiene que haber al menos un tramo.";
    const ts = tramos.map((t) => ({ d: parseFloat(t.desde), h: parseFloat(t.hasta), r: parseFloat(t.recargo) }));
    if (ts.some((t) => isNaN(t.d) || isNaN(t.h) || isNaN(t.r))) return "Completá todos los campos.";
    if (ts.some((t) => t.r < 0)) return "Los recargos no pueden ser negativos.";
    const ord = [...ts].sort((a, b) => a.d - b.d);
    if (ord.some((t) => t.d >= t.h)) return "Cada tramo necesita desde < hasta.";
    if (Math.abs(ord[0].d) > 1e-9) return "El primer tramo tiene que empezar en 0.";
    if (Math.abs(ord[ord.length - 1].h - 1) > 1e-9) return "El último tramo tiene que terminar en 1.";
    for (let i = 0; i < ord.length - 1; i++) {
      if (Math.abs(ord[i].h - ord[i + 1].d) > 1e-9) {
        return ord[i].h < ord[i + 1].d ? `Hay un hueco entre ${ord[i].h} y ${ord[i + 1].d}.` : `Los tramos se solapan en ${ord[i + 1].d}.`;
      }
    }
    return null;
  })();

  const guardarTramos = async () => {
    if (errorTramos) { toast.error(errorTramos); return; }
    setSavingTramos(true);
    try {
      const payload = tramos.map((t) => ({
        porcentaje_desde: parseFloat(t.desde),
        porcentaje_hasta: parseFloat(t.hasta),
        recargo: parseFloat(t.recargo) / 100, // % → fracción
      }));
      const r = await axios.post(`${BASE}/pricing_tramos_guardar.php`, new URLSearchParams({ tramos: JSON.stringify(payload) }));
      if (r.data?.success) { toast.success("Tramos guardados"); cargar(); }
      else toast.error(r.data?.mensaje || "Error");
    } catch { toast.error("Error de conexión"); }
    finally { setSavingTramos(false); }
  };

  if (loading) return <div style={S.page}><p style={{ textAlign: "center", color: "#aaa", padding: 40 }}>Cargando…</p></div>;

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <p style={S.title}>Calculadora de precios</p>
        <p style={S.sub}>Configuración del motor de precios de separadores: el popup "⚡ Precio de lista" de las cotizaciones usa estos valores. Los cambios no afectan cotizaciones ya creadas.</p>

        {/* ── Parámetro global ── */}
        <div style={S.card}>
          <p style={S.cardTitle}>Costo logístico por pallet</p>
          <p style={S.cardDesc}>Se usa para derivar el precio sin logística y como costo de "Directo desde Bs. As.".</p>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 14, color: "#666", fontWeight: 600 }}>$</span>
            <input style={{ ...S.input, width: 140 }} type="number" min="0" value={costoLog} onChange={(e) => setCostoLog(e.target.value)} />
            <button style={S.btnPrimary} onClick={guardarCosto}>Guardar</button>
          </div>
        </div>

        {/* ── Productos ── */}
        <div style={S.card}>
          <p style={S.cardTitle}>Productos (separadores)</p>
          <p style={S.cardDesc}>Precio de lista unitario para pallet completo, y unidades por pallet.</p>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Nombre</th>
              <th style={{ ...S.th, textAlign: "right" }}>Cant. por pallet</th>
              <th style={{ ...S.th, textAlign: "right" }}>Precio de lista</th>
              <th style={{ ...S.th, textAlign: "right" }}>Acciones</th>
            </tr></thead>
            <tbody>
              {productos.map((p, i) => (
                prodEdit?.id === p.id ? (
                  <tr key={p.id} style={{ background: "#fdf5fa" }}>
                    <td style={S.td}><input style={{ ...S.input, width: "95%" }} value={prodEdit.nombre} onChange={(e) => setProdEdit({ ...prodEdit, nombre: e.target.value })} /></td>
                    <td style={S.td}><input style={{ ...S.input, width: 90 }} type="number" value={prodEdit.cantidad_pallet} onChange={(e) => setProdEdit({ ...prodEdit, cantidad_pallet: e.target.value })} /></td>
                    <td style={S.td}><input style={{ ...S.input, width: 90 }} type="number" step="0.01" value={prodEdit.precio_lista} onChange={(e) => setProdEdit({ ...prodEdit, precio_lista: e.target.value })} /></td>
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button style={{ ...S.btnIcon, color: "#9f0059", fontWeight: 700 }} onClick={guardarProducto}>✓ Guardar</button>
                      <button style={{ ...S.btnIcon, color: "#888" }} onClick={() => setProdEdit(null)}>✕</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{p.nombre}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>{parseInt(p.cantidad_pallet).toLocaleString("es-AR")}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>${parseFloat(p.precio_lista).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</td>
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button style={{ ...S.btnIcon, color: "#555" }} title="Editar" onClick={() => setProdEdit({ id: p.id, nombre: p.nombre, cantidad_pallet: p.cantidad_pallet, precio_lista: p.precio_lista })}>✏️</button>
                      <button style={{ ...S.btnIcon, color: "#dc2626" }} title="Eliminar" onClick={() => eliminarProducto(p)}>🗑</button>
                    </td>
                  </tr>
                )
              ))}
              {prodEdit && !prodEdit.id && (
                <tr style={{ background: "#fdf5fa" }}>
                  <td style={S.td}><input style={{ ...S.input, width: "95%" }} placeholder="Nombre del separador" value={prodEdit.nombre} onChange={(e) => setProdEdit({ ...prodEdit, nombre: e.target.value })} autoFocus /></td>
                  <td style={S.td}><input style={{ ...S.input, width: 90 }} type="number" placeholder="25000" value={prodEdit.cantidad_pallet} onChange={(e) => setProdEdit({ ...prodEdit, cantidad_pallet: e.target.value })} /></td>
                  <td style={S.td}><input style={{ ...S.input, width: 90 }} type="number" step="0.01" placeholder="75.00" value={prodEdit.precio_lista} onChange={(e) => setProdEdit({ ...prodEdit, precio_lista: e.target.value })} /></td>
                  <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={{ ...S.btnIcon, color: "#9f0059", fontWeight: 700 }} onClick={guardarProducto}>✓ Guardar</button>
                    <button style={{ ...S.btnIcon, color: "#888" }} onClick={() => setProdEdit(null)}>✕</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {!prodEdit && (
            <button style={{ ...S.btnDashed, marginTop: 12 }} onClick={() => setProdEdit({ id: null, nombre: "", cantidad_pallet: "", precio_lista: "" })}>
              + Agregar producto
            </button>
          )}
        </div>

        {/* ── Formas de entrega ── */}
        <div style={S.card}>
          <p style={S.cardTitle}>Formas de entrega</p>
          <p style={S.cardDesc}>El tipo de cálculo define la fórmula. "Directo Bs. As." usa el costo logístico global (no carga costo propio).</p>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Nombre</th>
              <th style={S.th}>Tipo de cálculo</th>
              <th style={{ ...S.th, textAlign: "right" }}>Costo fijo</th>
              <th style={{ ...S.th, textAlign: "right" }}>Acciones</th>
            </tr></thead>
            <tbody>
              {formas.map((f, i) => (
                formaEdit?.id === f.id ? (
                  <tr key={f.id} style={{ background: "#fdf5fa" }}>
                    <td style={S.td}><input style={{ ...S.input, width: "95%" }} value={formaEdit.nombre} onChange={(e) => setFormaEdit({ ...formaEdit, nombre: e.target.value })} /></td>
                    <td style={S.td}>
                      <select style={S.input} value={formaEdit.tipo_calculo} onChange={(e) => setFormaEdit({ ...formaEdit, tipo_calculo: e.target.value })}>
                        {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {formaEdit.tipo_calculo === "DIRECTO_BSAS"
                        ? <span style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>usa costo global</span>
                        : <input style={{ ...S.input, width: 100 }} type="number" step="0.01" value={formaEdit.costo_fijo ?? ""} onChange={(e) => setFormaEdit({ ...formaEdit, costo_fijo: e.target.value })} />}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button style={{ ...S.btnIcon, color: "#9f0059", fontWeight: 700 }} onClick={guardarForma}>✓ Guardar</button>
                      <button style={{ ...S.btnIcon, color: "#888" }} onClick={() => setFormaEdit(null)}>✕</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={f.id} style={{ background: i % 2 === 0 ? "#fff" : "#fdf8fb" }}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{f.nombre}</td>
                    <td style={S.td}>{TIPOS.find((t) => t.value === f.tipo_calculo)?.label ?? f.tipo_calculo}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {f.costo_fijo === null
                        ? <span style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>usa costo global</span>
                        : "$" + parseFloat(f.costo_fijo).toLocaleString("es-AR")}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button style={{ ...S.btnIcon, color: "#555" }} title="Editar" onClick={() => setFormaEdit({ id: f.id, nombre: f.nombre, tipo_calculo: f.tipo_calculo, costo_fijo: f.costo_fijo ?? "" })}>✏️</button>
                      <button style={{ ...S.btnIcon, color: "#dc2626" }} title="Eliminar" onClick={() => eliminarForma(f)}>🗑</button>
                    </td>
                  </tr>
                )
              ))}
              {formaEdit && !formaEdit.id && (
                <tr style={{ background: "#fdf5fa" }}>
                  <td style={S.td}><input style={{ ...S.input, width: "95%" }} placeholder="Nombre" value={formaEdit.nombre} onChange={(e) => setFormaEdit({ ...formaEdit, nombre: e.target.value })} autoFocus /></td>
                  <td style={S.td}>
                    <select style={S.input} value={formaEdit.tipo_calculo} onChange={(e) => setFormaEdit({ ...formaEdit, tipo_calculo: e.target.value })}>
                      {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    {formaEdit.tipo_calculo === "DIRECTO_BSAS"
                      ? <span style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>usa costo global</span>
                      : <input style={{ ...S.input, width: 100 }} type="number" step="0.01" placeholder="0" value={formaEdit.costo_fijo ?? ""} onChange={(e) => setFormaEdit({ ...formaEdit, costo_fijo: e.target.value })} />}
                  </td>
                  <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={{ ...S.btnIcon, color: "#9f0059", fontWeight: 700 }} onClick={guardarForma}>✓ Guardar</button>
                    <button style={{ ...S.btnIcon, color: "#888" }} onClick={() => setFormaEdit(null)}>✕</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {!formaEdit && (
            <button style={{ ...S.btnDashed, marginTop: 12 }} onClick={() => setFormaEdit({ id: null, nombre: "", tipo_calculo: "NORMAL", costo_fijo: "" })}>
              + Agregar forma de entrega
            </button>
          )}
        </div>

        {/* ── Tramos de recargo ── */}
        <div style={S.card}>
          <p style={S.cardTitle}>Tramos de recargo</p>
          <p style={S.cardDesc}>
            Recargo por porcentaje del pallet, convención [desde, hasta): desde inclusivo, hasta exclusivo.
            Los tramos tienen que cubrir de 0 a 1 sin huecos ni solapamientos.
          </p>
          {tramos.map((t) => (
            <div key={t._k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: "#888" }}>de</span>
              <input style={{ ...S.input, width: 80 }} type="number" step="0.01" min="0" max="1" value={t.desde} onChange={(e) => updTramo(t._k, "desde", e.target.value)} />
              <span style={{ fontSize: 12, color: "#888" }}>a</span>
              <input style={{ ...S.input, width: 80 }} type="number" step="0.01" min="0" max="1" value={t.hasta} onChange={(e) => updTramo(t._k, "hasta", e.target.value)} />
              <span style={{ fontSize: 12, color: "#888" }}>→ recargo</span>
              <input style={{ ...S.input, width: 70 }} type="number" step="0.5" min="0" value={t.recargo} onChange={(e) => updTramo(t._k, "recargo", e.target.value)} />
              <span style={{ fontSize: 12, color: "#888" }}>%</span>
              <button style={{ ...S.btnIcon, color: "#c0396a" }} title="Quitar tramo" onClick={() => delTramo(t._k)}>✕</button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
            <button style={S.btnDashed} onClick={addTramo}>+ Agregar tramo</button>
            <button
              style={{ ...S.btnPrimary, opacity: (errorTramos || savingTramos) ? 0.5 : 1, cursor: (errorTramos || savingTramos) ? "not-allowed" : "pointer" }}
              disabled={!!errorTramos || savingTramos}
              onClick={guardarTramos}
            >
              {savingTramos ? "Guardando…" : "Guardar tramos"}
            </button>
          </div>
          {errorTramos && <div style={S.error}>⚠ {errorTramos}</div>}
        </div>
      </div>
    </div>
  );
};

export default PricingAdmin;
